import { ENUMS } from '@rapyd/use-hcp-preview';
import store from 'redux/store';
import { updateDemoData, updateDemoMenu } from 'redux/actions/demo';
import { ICartItems } from 'models/cart';
import { trackSigUpClick } from 'utilities/analytics/demo';
import { openLinkInNewTab } from 'utilities/window';
import {
  CDN_IMAGES_URL,
  CHECKOUT_DEMO_DEFAULT_COLORS,
  CHECKOUT_DEMO_FX_RATE,
  CHECKOUT_DEMO_INITIAL_AMOUNT,
  CHECKOUT_DEMO_TRANSACTION_FEE,
  PreviewParamKeys
} from 'consts';

type DemoMenuParams = {
  ctaText: string;
  buttonColor: string;
  colors?: string[] | null;
  selectedCategories: string[] | null;
  showTaxesAndFees: boolean;
  showCartItems: boolean;
  showFx: boolean;
  showBillingAddress: boolean;
  language: string;
  previewApplePay: boolean;
  previewGooglePay: boolean;
};

export const calcCheckoutDemoCartItems = (params: {
  checkoutPage: Record<string, any>;
  amount: number;
}): ICartItems[] => {
  const { amount, checkoutPage } = params;
  return [
    ...checkoutPage.cart_items,
    {
      name: 'Yoga Bottle',
      amount: 19,
      quantity: 1,
      image: `${CDN_IMAGES_URL}/yoga_bottle.jpg`
    },
    {
      name: 'Yoga Mat',
      amount: amount - 19,
      quantity: 1,
      image: `${CDN_IMAGES_URL}/yoga_mat.jpg`
    }
  ];
};

export const calcCheckoutDemoInitialPaymentBreakdown = (amount: number) => {
  return {
    payment_fx_metadata: {
      amount,
      currency_code: 'EUR',
      country_code: 'GB'
    },
    cart_items: [],
    calculated_payment_fees: {}
  };
};

export const calcCheckoutDemoPaymentFees = (params: {
  checkoutPage: Record<string, any>;
  transactionFee: number;
}) => {
  const { checkoutPage, transactionFee } = params;
  return {
    ...checkoutPage,
    calculated_payment_fees: {
      ...checkoutPage.calculated_payment_fees,
      transaction_fee: transactionFee
    },
    payment_fx_metadata: {
      ...checkoutPage.payment_fx_metadata,
      amount: checkoutPage.payment_fx_metadata.amount + transactionFee
    }
  };
};

export const calcCheckoutDemoFx = (params: {
  checkoutPage: Record<string, any>;
  fxRate: number;
}) => {
  const { checkoutPage, fxRate } = params;
  return {
    ...checkoutPage.payment_fx_metadata,
    currency_code: 'GBP',
    fx_rate: fxRate,
    fixed_side: 'buy',
    merchant_requested_currency: 'EUR',
    merchant_requested_amount: checkoutPage.payment_fx_metadata.amount,
    amount: parseFloat((checkoutPage.payment_fx_metadata.amount / fxRate).toFixed(2))
  };
};

export const buildDemoUrl = () => {
  const { form: demoMenuParams, previewParams } = store.getState().demo?.checkout || {};

  const parsedDemoParams: DemoMenuParams = {
    ctaText: previewParams.ctaText,
    buttonColor: previewParams.buttonColor,
    selectedCategories: previewParams.categories,
    showTaxesAndFees: demoMenuParams.showTaxesAndFees,
    showCartItems: demoMenuParams.showCartItems,
    showFx: demoMenuParams.showFx,
    showBillingAddress: demoMenuParams.showBillingAddress,
    language: previewParams.language,
    previewApplePay: previewParams.previewApplePay,
    previewGooglePay: previewParams.previewGooglePay
  };

  const url = `${window.location.origin}/demo`;

  const queryParams = Object.entries(parsedDemoParams)
    .map(
      ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join('&');

  return `${url}?${queryParams}`;
};

export const getAndUpdateDemoMenuInitialValues = (): DemoMenuParams => {
  const queryParams = new URLSearchParams(window.location.search);

  const getValue = (param: string, defaultValue: string) => {
    const paramValue = queryParams.get(param);
    return paramValue !== null && paramValue !== '' ? paramValue : defaultValue;
  };

  const getCtaTextValue = (param: string, defaultValue: string) => {
    const { ctaTexts } = store.getState()?.demo?.checkout?.form || {};
    const paramValue = queryParams.get(param) || '';
    return ctaTexts?.includes(paramValue) ? paramValue : defaultValue;
  };

  const updatedDemoMenuInitialValues: DemoMenuParams = {
    ctaText: getCtaTextValue('ctaText', 'pay'),
    buttonColor: getValue('buttonColor', ''),
    colors: CHECKOUT_DEMO_DEFAULT_COLORS,
    selectedCategories:
      getValue('selectedCategories', '')?.split(',').filter(Boolean) || null,
    showTaxesAndFees: getValue('showTaxesAndFees', 'false') === 'true',
    showCartItems: getValue('showCartItems', 'true') === 'true',
    showFx: getValue('showFx', 'false') === 'true',
    showBillingAddress: getValue('showBillingAddress', 'false') === 'true',
    language: getValue('language', 'en-US'),
    previewApplePay: getValue('previewApplePay', 'true') === 'true',
    previewGooglePay: getValue('previewGooglePay', 'true') === 'true'
  };

  store.dispatch(updateDemoMenu(updatedDemoMenuInitialValues));
  return updatedDemoMenuInitialValues;
};

export const getAndUpdateDemoCheckoutInitialValues = (
  demoMenuInitialValues: DemoMenuParams
) => {
  const { showCartItems, showBillingAddress, showTaxesAndFees, showFx } =
    demoMenuInitialValues;

  const updatedCheckoutDemoData = {
    [PreviewParamKeys.ctaText]: demoMenuInitialValues.ctaText,
    [PreviewParamKeys.buttonColor]: demoMenuInitialValues.buttonColor,
    [PreviewParamKeys.pageType]: ENUMS.FEATURE_TYPES.Checkout,
    [PreviewParamKeys.view]: ENUMS.VIEWS.Desktop,
    [PreviewParamKeys.checkoutPage]: getCheckoutPageParams({
      showCartItems,
      showTaxesAndFees,
      showFx,
      showBillingAddress
    }),
    [PreviewParamKeys.language]: demoMenuInitialValues.language,
    [PreviewParamKeys.previewApplePay]: demoMenuInitialValues.previewApplePay,
    [PreviewParamKeys.previewGooglePay]: demoMenuInitialValues.previewGooglePay
  };
  store.dispatch(updateDemoData(updatedCheckoutDemoData));

  return updatedCheckoutDemoData;
};

export const getCheckoutPageParams = ({
  showCartItems,
  showTaxesAndFees,
  showFx,
  showBillingAddress
}: {
  showCartItems: boolean;
  showTaxesAndFees: boolean;
  showFx: boolean;
  showBillingAddress: boolean;
}) => {
  const amount = CHECKOUT_DEMO_INITIAL_AMOUNT;
  let checkoutPage = calcCheckoutDemoInitialPaymentBreakdown(amount) as Record<
    string,
    any
  >;

  if (showCartItems) {
    checkoutPage.cart_items = calcCheckoutDemoCartItems({ checkoutPage, amount });
  }

  if (showTaxesAndFees) {
    checkoutPage = calcCheckoutDemoPaymentFees({
      checkoutPage,
      transactionFee: CHECKOUT_DEMO_TRANSACTION_FEE
    });
  }

  if (showFx) {
    const fxRate = CHECKOUT_DEMO_FX_RATE;
    checkoutPage.payment_fx_metadata = calcCheckoutDemoFx({ checkoutPage, fxRate });
  }

  if (showBillingAddress) {
    checkoutPage.demo_billing_address_collect = showBillingAddress;
  }

  return checkoutPage;
};
export const updateDemoInitialValues = () => {
  const demoMenuInitialValues = getAndUpdateDemoMenuInitialValues();
  getAndUpdateDemoCheckoutInitialValues(demoMenuInitialValues);
};

export const onClickSignUpButton = () => {
  const { sign_up_cp_url: signUpUrl } = store.getState().global?.configData || {};

  trackSigUpClick();
  openLinkInNewTab(signUpUrl);
};
